import type { MultiselectProps } from '@risk-smart/themed-cloudscape-components/multiselect';
import CSMultiSelect from '@risk-smart/themed-cloudscape-components/multiselect';
import { forwardRef, useMemo, useState } from 'react';

import { filterOptionsByVisibleText } from '../option-label-filter/filterOptionsByVisibleText';
import styles from './style.module.scss';

const Multiselect = forwardRef<MultiselectProps.Ref, MultiselectProps>(
  (
    { filteringType = 'auto', options, onLoadItems, virtualScroll, ...props },
    ref
  ) => {
    const [filteringText, setFilteringText] = useState('');

    // See filterOptionsByVisibleText for why we override 'auto' filtering.
    const preFilter = filteringType === 'auto';

    const visibleOptions = useMemo(
      () =>
        preFilter
          ? filterOptionsByVisibleText(options, filteringText)
          : options,
      [options, filteringText, preFilter]
    );

    // When we pre-filter (manual mode), Cloudscape's virtual list keeps its
    // scroll window pinned to the unfiltered slice and ignores the narrowed
    // option array we feed it, so typing in the search appears to do nothing
    // and clearing leaves a stale window (RSP-6288). The two mechanisms are
    // incompatible — react-virtual@2 only re-windows when Cloudscape owns the
    // filtering ('auto'), which we deliberately don't use. Disable virtualScroll
    // whenever we pre-filter so the (correct) plain list renders. A very-high-
    // count list then renders in full on open — an acceptable trade-off for a
    // transient dropdown, since a working filter matters more than virtualizing.
    const effectiveVirtualScroll = preFilter ? false : virtualScroll;

    return (
      <div className={styles.select}>
        <CSMultiSelect
          {...props}
          virtualScroll={effectiveVirtualScroll}
          filteringType={preFilter ? 'manual' : filteringType}
          options={visibleOptions}
          onLoadItems={(event) => {
            if (preFilter) {
              setFilteringText(event.detail.filteringText ?? '');
            }
            onLoadItems?.(event);
          }}
          ref={ref}
        />
      </div>
    );
  }
);
Multiselect.displayName = 'Multiselect';

export default Multiselect;
